import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Plus, Trash2, ArrowLeft, ExternalLink } from "lucide-react";
import ImageUploader from "./ImageUploader";
import LayoutEditor from "./LayoutEditor";

// Reusable CTA editor
const INTERNAL_DESTINATIONS = [
  { url: "/", label: "Home — top of page" },
  { url: "/#properties", label: "Home — Our Getaways (collection)" },
  { url: "/#categories", label: "Home — Occasions / Categories" },
  { url: "/#story", label: "Home — Brand story" },
  { url: "/#testimonials", label: "Home — Testimonial" },
  { url: "/#why", label: "Home — Why Happy Retreats" },
  { url: "/#partner", label: "Home — Partner block" },
  { url: "/#gallery", label: "Home — Gallery" },
  { url: "/#stories", label: "Home — Stories teaser" },
  { url: "/#contact", label: "Home — Closing CTA" },
  { url: "/partner-with-us", label: "Partner With Us — page" },
  { url: "/partner-with-us#enquire", label: "Partner With Us — submit property form" },
  { url: "/blog", label: "Journal / Stories" },
];

let _destCache = null;
const useDestinations = () => {
  const [dests, setDests] = useState(_destCache || INTERNAL_DESTINATIONS);
  useEffect(() => {
    if (_destCache) return;
    Promise.all([
      api.get("/public/properties").catch(() => ({ data: [] })),
      api.get("/public/seo-pages").catch(() => ({ data: [] })),
    ]).then(([p, s]) => {
      const props = (p.data || []).flatMap((x) => [
        { url: `/${x.slug}`, label: `Getaway — ${x.name}` },
        { url: `/${x.slug}#gallery`, label: `Getaway — ${x.name} · Gallery` },
      ]);
      const seos = (s.data || []).map((x) => ({ url: `/${x.slug}`, label: `Landing page — ${x.h1 || x.slug}` }));
      _destCache = [...INTERNAL_DESTINATIONS, ...props, ...seos];
      setDests(_destCache);
    });
  }, []);
  return dests;
};

const CTAEditor = ({ value, onChange, label = "CTA button", testId }) => {
  const v = value || { enabled: false, label: "", url: "", action: "enquiry" };
  const set = (k, val) => onChange({ ...v, [k]: val });
  const dests = useDestinations();
  const isKnown = dests.some((d) => d.url === v.url);
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{label}</div>
        <label className="flex items-center gap-2 text-xs"><Switch checked={!!v.enabled} onCheckedChange={(x) => set("enabled", x)} data-testid={`${testId}-enabled`} /> {v.enabled ? "Enabled" : "Disabled"}</label>
      </div>
      <Input value={v.label || ""} onChange={(e) => set("label", e.target.value)} placeholder="Button label" data-testid={`${testId}-label`} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Select value={v.action || "enquiry"} onValueChange={(x) => set("action", x)}>
          <SelectTrigger data-testid={`${testId}-action`}><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="enquiry">Open enquiry modal</SelectItem>
            <SelectItem value="link">Link (internal or external)</SelectItem>
          </SelectContent>
        </Select>
        {v.action === "link" && (
          <div className="md:col-span-2">
            <Select value={isKnown ? v.url : "custom"} onValueChange={(x) => set("url", x === "custom" ? "" : x)}>
              <SelectTrigger data-testid={`${testId}-dest`}><SelectValue placeholder="Choose destination" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {dests.map((d) => <SelectItem key={d.url} value={d.url}>{d.label}</SelectItem>)}
                <SelectItem value="custom">Custom / external URL…</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {v.action === "link" && (
        isKnown ? (
          <div className="text-xs text-zinc-500">Links to <code className="bg-zinc-100 px-1 py-0.5 rounded">{v.url}</code></div>
        ) : (
          <Input value={v.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="Paste URL (e.g. https://... or /kai#gallery)" data-testid={`${testId}-url`} />
        )
      )}
    </div>
  );
};

// Simple repeater for {v, l} or {kw, title, body} arrays
const Repeater = ({ items = [], onChange, fields, label, testId }) => {
  const upd = (i, k, x) => { const n = [...items]; n[i] = { ...n[i], [k]: x }; onChange(n); };
  const rm = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, fields.reduce((a, f) => ({ ...a, [f.key]: "" }), {})]);
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium">{label}</div>
        <button type="button" onClick={add} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border rounded-md"><Plus className="w-3 h-3" /> Add</button>
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="bg-zinc-50 border rounded-md p-3 space-y-2">
            <div className="flex justify-between text-xs text-zinc-500">Item {i + 1}<button type="button" onClick={() => rm(i)} className="text-red-600 hover:underline">Remove</button></div>
            {fields.map((f) =>
              f.type === "textarea" ? (
                <Textarea key={f.key} value={it[f.key] || ""} onChange={(e) => upd(i, f.key, e.target.value)} placeholder={f.label} rows={f.rows || 3} />
              ) : f.type === "image" ? (
                <ImageUploader key={f.key} label={f.label} value={it[f.key] || ""} onChange={(x) => upd(i, f.key, x)} testId={`${testId}-${i}-${f.key}`} />
              ) : (
                <Input key={f.key} value={it[f.key] || ""} onChange={(e) => upd(i, f.key, e.target.value)} placeholder={f.label} />
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Paragraphs array of strings
const Paragraphs = ({ items = [], onChange }) => (
  <div>
    <div className="flex justify-between mb-2">
      <div className="text-sm font-medium">Paragraphs</div>
      <button type="button" onClick={() => onChange([...(items || []), ""])} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border rounded-md"><Plus className="w-3 h-3" /> Add</button>
    </div>
    <div className="space-y-2">
      {(items || []).map((p, i) => (
        <div key={i} className="flex gap-2">
          <Textarea value={p} onChange={(e) => { const n = [...items]; n[i] = e.target.value; onChange(n); }} rows={3} className="flex-1" />
          <button type="button" onClick={() => onChange(items.filter((_, x) => x !== i))} className="px-2 text-red-600"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  </div>
);

const SectionToggle = ({ section, onChange }) => (
  <label className="flex items-center gap-2 text-sm"><Switch checked={section?.enabled !== false} onCheckedChange={(v) => onChange({ ...section, enabled: v })} /> Section enabled</label>
);

const CtaToggle = ({ section, onChange, label = "Show CTA buttons / links", testId }) => (
  <label className="flex items-center gap-2 text-sm"><Switch checked={section?.show_cta !== false} onCheckedChange={(v) => onChange({ ...section, show_cta: v })} data-testid={testId} /> {label}</label>
);

// Sections map per page — declarative so we can grow easily
const HOME_SECTIONS = ["hero", "trust_strip", "brand_story", "properties_section", "categories", "testimonial_section", "why_section", "partner_block", "gallery_section", "blog_section", "closing_cta", "custom_sections"];
const PARTNER_SECTIONS = ["hero", "stats", "philosophy", "pillars", "steps", "models", "enquire"];

function HomeEditor({ c, set }) {
  const s = (k, v) => set({ ...c, [k]: v });
  return (
    <Tabs defaultValue="hero" className="mt-6">
      <TabsList className="flex-wrap h-auto">
        {HOME_SECTIONS.map((t) => <TabsTrigger key={t} value={t} className="capitalize" data-testid={`tab-${t}`}>{t.replace(/_/g, " ")}</TabsTrigger>)}
      </TabsList>

      <TabsContent value="custom_sections" className="space-y-4 mt-6">
        <p className="text-sm text-zinc-500">Build extra home-page sections with the drag-and-drop builder. They render after the Stories section, just before the closing CTA.</p>
        <LayoutEditor layout={c.layout || []} onChange={(v) => s("layout", v)} coreEnabled={false} />
      </TabsContent>

      <TabsContent value="hero" className="space-y-4 mt-6">
        <SectionToggle section={c.hero} onChange={(v) => s("hero", v)} />
        <CtaToggle section={c.hero} onChange={(v) => s("hero", v)} label="Show hero CTA buttons" testId="cta-toggle-hero" />
        <Input value={c.hero?.overline || ""} onChange={(e) => s("hero", { ...c.hero, overline: e.target.value })} placeholder="Overline" />
        <Input value={c.hero?.headline_pre || ""} onChange={(e) => s("hero", { ...c.hero, headline_pre: e.target.value })} placeholder="Headline (before accent)" />
        <Input value={c.hero?.headline_accent || ""} onChange={(e) => s("hero", { ...c.hero, headline_accent: e.target.value })} placeholder="Accent phrase (italicised)" />
        <Input value={c.hero?.headline_post || ""} onChange={(e) => s("hero", { ...c.hero, headline_post: e.target.value })} placeholder="Post-accent (usually .)" />
        <Textarea value={c.hero?.subheadline || ""} onChange={(e) => s("hero", { ...c.hero, subheadline: e.target.value })} placeholder="Subheadline" rows={3} />
        <ImageUploader label="Hero image" value={c.hero?.image_url} onChange={(v) => s("hero", { ...c.hero, image_url: v })} testId="home-hero-image" />
        <CTAEditor label="Primary CTA" value={c.hero?.primary_cta} onChange={(v) => s("hero", { ...c.hero, primary_cta: v })} testId="home-hero-primary" />
        <CTAEditor label="Secondary CTA" value={c.hero?.secondary_cta} onChange={(v) => s("hero", { ...c.hero, secondary_cta: v })} testId="home-hero-secondary" />
      </TabsContent>

      <TabsContent value="trust_strip" className="space-y-4 mt-6">
        <SectionToggle section={c.trust_strip} onChange={(v) => s("trust_strip", v)} />
        <Repeater items={c.trust_strip?.metrics || []} onChange={(v) => s("trust_strip", { ...c.trust_strip, metrics: v })}
          fields={[{ key: "value", label: "Value (e.g. 850+)" }, { key: "label", label: "Label (e.g. Celebrations hosted)" }]}
          label="Metrics" testId="trust-metrics" />
      </TabsContent>

      <TabsContent value="brand_story" className="space-y-4 mt-6">
        <SectionToggle section={c.brand_story} onChange={(v) => s("brand_story", v)} />
        <CtaToggle section={c.brand_story} onChange={(v) => s("brand_story", v)} label="Show story link (arrow CTA)" testId="cta-toggle-brand-story" />
        <Input value={c.brand_story?.overline || ""} onChange={(e) => s("brand_story", { ...c.brand_story, overline: e.target.value })} placeholder="Overline" />
        <Input value={c.brand_story?.headline_pre || ""} onChange={(e) => s("brand_story", { ...c.brand_story, headline_pre: e.target.value })} placeholder="Headline line 1" />
        <Input value={c.brand_story?.headline_accent || ""} onChange={(e) => s("brand_story", { ...c.brand_story, headline_accent: e.target.value })} placeholder="Headline line 2 (italicised)" />
        <Paragraphs items={c.brand_story?.paragraphs} onChange={(v) => s("brand_story", { ...c.brand_story, paragraphs: v })} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input value={c.brand_story?.link_label || ""} onChange={(e) => s("brand_story", { ...c.brand_story, link_label: e.target.value })} placeholder="Link label" />
          <Input value={c.brand_story?.link_url || ""} onChange={(e) => s("brand_story", { ...c.brand_story, link_url: e.target.value })} placeholder="Link URL" />
        </div>
      </TabsContent>

      <TabsContent value="properties_section" className="space-y-4 mt-6">
        <SectionToggle section={c.properties_section} onChange={(v) => s("properties_section", v)} />
        <CtaToggle section={c.properties_section} onChange={(v) => s("properties_section", v)} label='Show "View getaway" links on cards' testId="cta-toggle-properties" />
        <Input value={c.properties_section?.overline || ""} onChange={(e) => s("properties_section", { ...c.properties_section, overline: e.target.value })} placeholder="Overline" />
        <Input value={c.properties_section?.headline || ""} onChange={(e) => s("properties_section", { ...c.properties_section, headline: e.target.value })} placeholder="Headline" />
        <Textarea value={c.properties_section?.caption || ""} onChange={(e) => s("properties_section", { ...c.properties_section, caption: e.target.value })} placeholder="Caption" rows={3} />
      </TabsContent>

      <TabsContent value="categories" className="space-y-4 mt-6">
        <SectionToggle section={c.categories} onChange={(v) => s("categories", v)} />
        <CtaToggle section={c.categories} onChange={(v) => s("categories", v)} label='Show "Explore" links on cards' testId="cta-toggle-categories" />
        <Input value={c.categories?.overline || ""} onChange={(e) => s("categories", { ...c.categories, overline: e.target.value })} placeholder="Overline" />
        <Input value={c.categories?.headline || ""} onChange={(e) => s("categories", { ...c.categories, headline: e.target.value })} placeholder="Headline" />
        <Textarea value={c.categories?.caption || ""} onChange={(e) => s("categories", { ...c.categories, caption: e.target.value })} placeholder="Caption" rows={2} />
        <Repeater items={c.categories?.items || []} onChange={(v) => s("categories", { ...c.categories, items: v })}
          fields={[
            { key: "kw", label: "Keyword tag (CELEBRATE)" },
            { key: "icon", label: "Icon name (Heart, Building2, Mountain, etc.)" },
            { key: "label", label: "Label" },
            { key: "url", label: "URL" },
            { key: "caption", label: "Caption", type: "textarea" },
            { key: "image", label: "Image", type: "image" },
          ]} label="Category cards" testId="categories-editor" />
      </TabsContent>

      <TabsContent value="testimonial_section" className="space-y-4 mt-6">
        <SectionToggle section={c.testimonial_section} onChange={(v) => s("testimonial_section", v)} />
        <Input value={c.testimonial_section?.overline || ""} onChange={(e) => s("testimonial_section", { ...c.testimonial_section, overline: e.target.value })} placeholder="Overline" />
        <p className="text-xs text-zinc-500">The quote itself is pulled from the featured Testimonial (Admin → Testimonials).</p>
      </TabsContent>

      <TabsContent value="why_section" className="space-y-4 mt-6">
        <SectionToggle section={c.why_section} onChange={(v) => s("why_section", v)} />
        <Input value={c.why_section?.overline || ""} onChange={(e) => s("why_section", { ...c.why_section, overline: e.target.value })} placeholder="Overline" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input value={c.why_section?.headline_pre || ""} onChange={(e) => s("why_section", { ...c.why_section, headline_pre: e.target.value })} placeholder="Headline pre" />
          <Input value={c.why_section?.headline_accent || ""} onChange={(e) => s("why_section", { ...c.why_section, headline_accent: e.target.value })} placeholder="Accent (italic)" />
          <Input value={c.why_section?.headline_post || ""} onChange={(e) => s("why_section", { ...c.why_section, headline_post: e.target.value })} placeholder="Headline post" />
        </div>
        <Repeater items={c.why_section?.items || []} onChange={(v) => s("why_section", { ...c.why_section, items: v })}
          fields={[{ key: "kw", label: "Number (01)" }, { key: "title", label: "Title" }, { key: "body", label: "Body", type: "textarea" }]}
          label="Reasons" testId="why-editor" />
      </TabsContent>

      <TabsContent value="partner_block" className="space-y-4 mt-6">
        <SectionToggle section={c.partner_block} onChange={(v) => s("partner_block", v)} />
        <CtaToggle section={c.partner_block} onChange={(v) => s("partner_block", v)} label="Show CTA buttons" testId="cta-toggle-partner-block" />
        <Input value={c.partner_block?.overline || ""} onChange={(e) => s("partner_block", { ...c.partner_block, overline: e.target.value })} placeholder="Overline" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Input value={c.partner_block?.headline_pre || ""} onChange={(e) => s("partner_block", { ...c.partner_block, headline_pre: e.target.value })} placeholder="Headline line 1" />
          <Input value={c.partner_block?.headline_accent || ""} onChange={(e) => s("partner_block", { ...c.partner_block, headline_accent: e.target.value })} placeholder="Headline line 2 (italic)" />
        </div>
        <Textarea value={c.partner_block?.body || ""} onChange={(e) => s("partner_block", { ...c.partner_block, body: e.target.value })} placeholder="Body" rows={4} />
        <ImageUploader label="Background image" value={c.partner_block?.image_url} onChange={(v) => s("partner_block", { ...c.partner_block, image_url: v })} testId="partner-block-image" />
        <Repeater items={c.partner_block?.pillars || []} onChange={(v) => s("partner_block", { ...c.partner_block, pillars: v })}
          fields={[{ key: "icon", label: "Icon name" }, { key: "title", label: "Title" }, { key: "body", label: "Body", type: "textarea" }]}
          label="Pillars" testId="partner-pillars" />
        <CTAEditor label="Primary CTA" value={c.partner_block?.primary_cta} onChange={(v) => s("partner_block", { ...c.partner_block, primary_cta: v })} testId="partner-block-primary" />
        <CTAEditor label="Secondary CTA" value={c.partner_block?.secondary_cta} onChange={(v) => s("partner_block", { ...c.partner_block, secondary_cta: v })} testId="partner-block-secondary" />
      </TabsContent>

      <TabsContent value="gallery_section" className="space-y-4 mt-6">
        <SectionToggle section={c.gallery_section} onChange={(v) => s("gallery_section", v)} />
        <CtaToggle section={c.gallery_section} onChange={(v) => s("gallery_section", v)} label='Show "All stories" link' testId="cta-toggle-gallery" />
        <Input value={c.gallery_section?.overline || ""} onChange={(e) => s("gallery_section", { ...c.gallery_section, overline: e.target.value })} placeholder="Overline" />
        <Input value={c.gallery_section?.headline || ""} onChange={(e) => s("gallery_section", { ...c.gallery_section, headline: e.target.value })} placeholder="Headline" />
        <p className="text-xs text-zinc-500">Images are pulled from Admin → Gallery.</p>
      </TabsContent>

      <TabsContent value="blog_section" className="space-y-4 mt-6">
        <SectionToggle section={c.blog_section} onChange={(v) => s("blog_section", v)} />
        <CtaToggle section={c.blog_section} onChange={(v) => s("blog_section", v)} label='Show "All stories" link' testId="cta-toggle-blog" />
        <Input value={c.blog_section?.overline || ""} onChange={(e) => s("blog_section", { ...c.blog_section, overline: e.target.value })} placeholder="Overline" />
        <Input value={c.blog_section?.headline || ""} onChange={(e) => s("blog_section", { ...c.blog_section, headline: e.target.value })} placeholder="Headline" />
      </TabsContent>

      <TabsContent value="closing_cta" className="space-y-4 mt-6">
        <SectionToggle section={c.closing_cta} onChange={(v) => s("closing_cta", v)} />
        <CtaToggle section={c.closing_cta} onChange={(v) => s("closing_cta", v)} label="Show CTA buttons" testId="cta-toggle-closing" />
        <Input value={c.closing_cta?.headline_line1 || ""} onChange={(e) => s("closing_cta", { ...c.closing_cta, headline_line1: e.target.value })} placeholder="Headline line 1" />
        <Input value={c.closing_cta?.headline_line2 || ""} onChange={(e) => s("closing_cta", { ...c.closing_cta, headline_line2: e.target.value })} placeholder="Headline line 2" />
        <Textarea value={c.closing_cta?.body || ""} onChange={(e) => s("closing_cta", { ...c.closing_cta, body: e.target.value })} placeholder="Body" rows={3} />
        <CTAEditor label="Primary CTA" value={c.closing_cta?.primary_cta} onChange={(v) => s("closing_cta", { ...c.closing_cta, primary_cta: v })} testId="closing-primary" />
        <CTAEditor label="Secondary CTA" value={c.closing_cta?.secondary_cta} onChange={(v) => s("closing_cta", { ...c.closing_cta, secondary_cta: v })} testId="closing-secondary" />
      </TabsContent>
    </Tabs>
  );
}

function PartnerEditor({ c, set }) {
  const s = (k, v) => set({ ...c, [k]: v });
  return (
    <Tabs defaultValue="hero" className="mt-6">
      <TabsList className="flex-wrap h-auto">
        {PARTNER_SECTIONS.map((t) => <TabsTrigger key={t} value={t} className="capitalize" data-testid={`ptab-${t}`}>{t.replace(/_/g, " ")}</TabsTrigger>)}
      </TabsList>

      <TabsContent value="hero" className="space-y-4 mt-6">
        <SectionToggle section={c.hero} onChange={(v) => s("hero", v)} />
        <Input value={c.hero?.overline || ""} onChange={(e) => s("hero", { ...c.hero, overline: e.target.value })} placeholder="Overline" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input value={c.hero?.headline_pre || ""} onChange={(e) => s("hero", { ...c.hero, headline_pre: e.target.value })} placeholder="Headline pre" />
          <Input value={c.hero?.headline_accent || ""} onChange={(e) => s("hero", { ...c.hero, headline_accent: e.target.value })} placeholder="Accent (italic)" />
          <Input value={c.hero?.headline_post || ""} onChange={(e) => s("hero", { ...c.hero, headline_post: e.target.value })} placeholder="Post" />
        </div>
        <Textarea value={c.hero?.subheadline || ""} onChange={(e) => s("hero", { ...c.hero, subheadline: e.target.value })} rows={3} placeholder="Subheadline" />
        <ImageUploader label="Hero image" value={c.hero?.image_url} onChange={(v) => s("hero", { ...c.hero, image_url: v })} testId="partner-hero-image" />
        <CTAEditor label="Primary CTA" value={c.hero?.primary_cta} onChange={(v) => s("hero", { ...c.hero, primary_cta: v })} testId="p-hero-primary" />
        <CTAEditor label="Secondary CTA" value={c.hero?.secondary_cta} onChange={(v) => s("hero", { ...c.hero, secondary_cta: v })} testId="p-hero-secondary" />
      </TabsContent>

      <TabsContent value="stats" className="space-y-4 mt-6">
        <SectionToggle section={c.stats} onChange={(v) => s("stats", v)} />
        <Repeater items={c.stats?.items || []} onChange={(v) => s("stats", { ...c.stats, items: v })}
          fields={[{ key: "v", label: "Value" }, { key: "l", label: "Label" }]} label="Stats" testId="p-stats" />
      </TabsContent>

      <TabsContent value="philosophy" className="space-y-4 mt-6">
        <SectionToggle section={c.philosophy} onChange={(v) => s("philosophy", v)} />
        <Input value={c.philosophy?.overline || ""} onChange={(e) => s("philosophy", { ...c.philosophy, overline: e.target.value })} placeholder="Overline" />
        <Input value={c.philosophy?.headline_line1 || ""} onChange={(e) => s("philosophy", { ...c.philosophy, headline_line1: e.target.value })} placeholder="Headline line 1" />
        <Input value={c.philosophy?.headline_line2 || ""} onChange={(e) => s("philosophy", { ...c.philosophy, headline_line2: e.target.value })} placeholder="Headline line 2" />
        <Paragraphs items={c.philosophy?.paragraphs} onChange={(v) => s("philosophy", { ...c.philosophy, paragraphs: v })} />
      </TabsContent>

      <TabsContent value="pillars" className="space-y-4 mt-6">
        <SectionToggle section={c.pillars} onChange={(v) => s("pillars", v)} />
        <Input value={c.pillars?.overline || ""} onChange={(e) => s("pillars", { ...c.pillars, overline: e.target.value })} placeholder="Overline" />
        <Input value={c.pillars?.headline || ""} onChange={(e) => s("pillars", { ...c.pillars, headline: e.target.value })} placeholder="Headline" />
        <Repeater items={c.pillars?.items || []} onChange={(v) => s("pillars", { ...c.pillars, items: v })}
          fields={[{ key: "icon", label: "Icon" }, { key: "title", label: "Title" }, { key: "body", label: "Body", type: "textarea" }]}
          label="Pillars" testId="p-pillars" />
      </TabsContent>

      <TabsContent value="steps" className="space-y-4 mt-6">
        <SectionToggle section={c.steps} onChange={(v) => s("steps", v)} />
        <Input value={c.steps?.overline || ""} onChange={(e) => s("steps", { ...c.steps, overline: e.target.value })} placeholder="Overline" />
        <Input value={c.steps?.headline || ""} onChange={(e) => s("steps", { ...c.steps, headline: e.target.value })} placeholder="Headline" />
        <Repeater items={c.steps?.items || []} onChange={(v) => s("steps", { ...c.steps, items: v })}
          fields={[{ key: "kw", label: "Number (01)" }, { key: "title", label: "Title" }, { key: "body", label: "Body", type: "textarea" }]}
          label="Steps" testId="p-steps" />
      </TabsContent>

      <TabsContent value="models" className="space-y-4 mt-6">
        <SectionToggle section={c.models} onChange={(v) => s("models", v)} />
        <Input value={c.models?.overline || ""} onChange={(e) => s("models", { ...c.models, overline: e.target.value })} placeholder="Overline" />
        <Input value={c.models?.headline || ""} onChange={(e) => s("models", { ...c.models, headline: e.target.value })} placeholder="Headline" />
        <Repeater items={c.models?.items || []} onChange={(v) => s("models", { ...c.models, items: v })}
          fields={[{ key: "name", label: "Model name" }, { key: "caption", label: "Caption", type: "textarea" }, { key: "best", label: "Best for" }]}
          label="Commercial models" testId="p-models" />
      </TabsContent>

      <TabsContent value="enquire" className="space-y-4 mt-6">
        <SectionToggle section={c.enquire} onChange={(v) => s("enquire", v)} />
        <Input value={c.enquire?.overline || ""} onChange={(e) => s("enquire", { ...c.enquire, overline: e.target.value })} placeholder="Overline" />
        <Input value={c.enquire?.headline || ""} onChange={(e) => s("enquire", { ...c.enquire, headline: e.target.value })} placeholder="Headline" />
        <Textarea value={c.enquire?.body || ""} onChange={(e) => s("enquire", { ...c.enquire, body: e.target.value })} rows={3} placeholder="Body" />
        <Repeater items={c.enquire?.assurances || []} onChange={(v) => s("enquire", { ...c.enquire, assurances: v })}
          fields={[{ key: "title", label: "Title" }, { key: "sub", label: "Subtitle" }]} label="Trust assurances" testId="p-assurances" />
      </TabsContent>
    </Tabs>
  );
}

export default function PageCMS({ pageId: pageIdProp }) {
  const { pageId: pageIdParam } = useParams();
  const pageId = pageIdProp || pageIdParam;
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(`/admin/content/${pageId}`).then((r) => setC(r.data)); }, [pageId]);
  if (!c) return <div>Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/content/${pageId}`, c);
      toast.success(`${pageTitle} saved`, { duration: 3000 });
    } catch (e) {
      toast.error("Save failed", { duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = pageId === "home" ? "/" : "/partner-with-us";
  const pageTitle = pageId === "home" ? "Home page" : "Partner With Us page";

  return (
    <div>
      <Link to="/admin" className="text-xs text-zinc-500 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Dashboard</Link>
      <div className="flex items-end justify-between mt-3 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Page content</div>
          <h1 className="font-display text-4xl mt-2">{pageTitle}</h1>
          <p className="text-sm text-zinc-500 mt-1">Edit every section, every headline, every CTA. Any section can be toggled off.</p>
        </div>
        <div className="flex items-center gap-3">
          <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 px-3 py-2 border rounded-md"><ExternalLink className="w-3 h-3" /> Preview</a>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm rounded-md" data-testid="page-save">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      {pageId === "home" ? <HomeEditor c={c} set={setC} /> : <PartnerEditor c={c} set={setC} />}
    </div>
  );
}
